/**
 * Artifact Settings Editor
 * Configure artifact templates and taxonomy for the account
 * Full template editor with visual schema editing
 */

import { useState, useEffect, useMemo } from 'react'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { useSessionStore } from '@/stores/sessionStore'
import { artifactTemplatesApi } from '@/lib/api/client'
import type {
  ArtifactTemplate,
  ArtifactTypeEnum,
  ArtifactDomain,
  CanvasTemplateStructure,
  DocTemplateStructure,
  CanvasSection,
  DocSection,
  EntityShortcut,
} from '@ispot/shared'
import {
  ChevronDown,
  ChevronRight,
  Loader2,
  FileText,
  Layout,
  Calendar,
  Paperclip,
  RotateCcw,
  GripVertical,
  Pencil,
  Plus,
  X,
  Users,
} from 'lucide-react'

// ============================================================================
// TYPES & CONSTANTS
// ============================================================================

const TYPE_ICONS: Record<ArtifactTypeEnum, React.ReactNode> = {
  Canvas: <Layout className="w-4 h-4" />,
  Doc: <FileText className="w-4 h-4" />,
  Plan: <Calendar className="w-4 h-4" />,
  Evidence: <Paperclip className="w-4 h-4" />,
}

const TYPE_LABELS: Record<ArtifactTypeEnum, string> = {
  Canvas: 'Canvas',
  Doc: 'Document',
  Plan: 'Plan',
  Evidence: 'Evidence',
}

const DOMAIN_COLORS: Record<ArtifactDomain, { bg: string; text: string; border: string }> = {
  Market: { bg: 'rgba(92, 138, 107, 0.15)', text: '#5c8a6b', border: 'rgba(92, 138, 107, 0.3)' },
  Business: { bg: 'rgba(49, 150, 226, 0.15)', text: '#3196e2', border: 'rgba(49, 150, 226, 0.3)' },
  Solution: { bg: 'rgba(255, 192, 95, 0.15)', text: '#ffc05f', border: 'rgba(255, 192, 95, 0.3)' },
}

type EditorTab = 'details' | 'schema' | 'defaults'

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export function ArtifactSettings() {
  const { accountId } = useSessionStore()
  const [templates, setTemplates] = useState<ArtifactTemplate[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null)
  const [expandedTypes, setExpandedTypes] = useState<Set<string>>(new Set(['Canvas', 'Doc']))
  const [expandedSubTypes, setExpandedSubTypes] = useState<Set<string>>(new Set())

  // Load templates
  useEffect(() => {
    if (!accountId) return

    const load = async () => {
      try {
        setLoading(true)
        const result = await artifactTemplatesApi.list(accountId)
        setTemplates(result.templates)
        setError(null)
        // Auto-select first template
        if (result.templates.length > 0 && !selectedTemplateId) {
          setSelectedTemplateId(result.templates[0].id)
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load templates')
      } finally {
        setLoading(false)
      }
    }

    load()
  }, [accountId])

  // Group templates by type and subType
  const templateTree = useMemo(() => {
    const tree: Record<ArtifactTypeEnum, Record<string, ArtifactTemplate[]>> = {
      Canvas: {},
      Doc: {},
      Plan: {},
      Evidence: {},
    }

    templates.forEach((template) => {
      if (!tree[template.type][template.subType]) {
        tree[template.type][template.subType] = []
      }
      tree[template.type][template.subType].push(template)
    })

    return tree
  }, [templates])

  const selectedTemplate = useMemo(
    () => templates.find((t) => t.id === selectedTemplateId) || null,
    [templates, selectedTemplateId]
  )

  const handleTemplateUpdate = (updated: ArtifactTemplate) => {
    setTemplates((prev) => prev.map((t) => (t.id === updated.id ? updated : t)))
  }

  const toggleType = (type: string) => {
    setExpandedTypes((prev) => {
      const next = new Set(prev)
      if (next.has(type)) next.delete(type)
      else next.add(type)
      return next
    })
  }

  const toggleSubType = (key: string) => {
    setExpandedSubTypes((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  if (loading) {
    return (
      <Card>
        <CardContent className="py-12">
          <div className="flex items-center justify-center gap-2 text-text-muted">
            <Loader2 className="w-4 h-4 animate-spin" />
            <span>Loading artifact templates...</span>
          </div>
        </CardContent>
      </Card>
    )
  }

  if (error) {
    return (
      <Card>
        <CardContent className="py-12">
          <div className="flex flex-col items-center gap-4 text-text-muted">
            <span>{error}</span>
            <Button variant="outline" onClick={() => window.location.reload()}>
              <RotateCcw className="w-4 h-4" />
              Retry
            </Button>
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="flex gap-0 h-[calc(100vh-180px)] min-h-[600px]">
      {/* Left Sidebar - Tree View */}
      <div className="w-72 flex-shrink-0 border-r border-border bg-warm-100 flex flex-col">
        <div className="p-4 border-b border-border">
          <h2 className="font-display font-semibold text-base">Artifact Types</h2>
          <p className="text-xs text-text-muted mt-1">Configure types, subtypes, and templates</p>
        </div>

        <div className="flex-1 overflow-y-auto p-3">
          {(['Canvas', 'Doc', 'Plan', 'Evidence'] as ArtifactTypeEnum[]).map((type) => {
            const subTypes = Object.keys(templateTree[type])
            const totalCount = subTypes.reduce(
              (sum, st) => sum + templateTree[type][st].length,
              0
            )
            if (totalCount === 0) return null

            const isTypeExpanded = expandedTypes.has(type)

            return (
              <div key={type} className="mb-1">
                {/* Type Header */}
                <button
                  onClick={() => toggleType(type)}
                  className="flex items-center gap-2 w-full p-2 rounded-md hover:bg-warm-200 transition-colors"
                >
                  {isTypeExpanded ? (
                    <ChevronDown className="w-4 h-4 text-text-muted" />
                  ) : (
                    <ChevronRight className="w-4 h-4 text-text-muted" />
                  )}
                  <span className="text-business">{TYPE_ICONS[type]}</span>
                  <span className="font-medium text-sm flex-1 text-left">{TYPE_LABELS[type]}</span>
                  <Badge variant="outline" className="text-xs h-5 px-1.5">
                    {totalCount}
                  </Badge>
                </button>

                {/* SubTypes */}
                {isTypeExpanded && (
                  <div className="ml-4">
                    {subTypes.map((subType) => {
                      const subTypeKey = `${type}-${subType}`
                      const isSubTypeExpanded = expandedSubTypes.has(subTypeKey)
                      const subTypeTemplates = templateTree[type][subType]

                      return (
                        <div key={subTypeKey}>
                          {/* SubType Header */}
                          <button
                            onClick={() => toggleSubType(subTypeKey)}
                            className="flex items-center gap-2 w-full p-2 rounded-md hover:bg-warm-200 transition-colors"
                          >
                            {isSubTypeExpanded ? (
                              <ChevronDown className="w-3 h-3 text-text-muted" />
                            ) : (
                              <ChevronRight className="w-3 h-3 text-text-muted" />
                            )}
                            <span className="text-sm flex-1 text-left">{subType}</span>
                            <Badge variant="outline" className="text-xs h-5 px-1.5">
                              {subTypeTemplates.length}
                            </Badge>
                          </button>

                          {/* Templates */}
                          {isSubTypeExpanded && (
                            <div className="ml-4">
                              {subTypeTemplates.map((template) => {
                                const isSelected = template.id === selectedTemplateId
                                const domainStyle = DOMAIN_COLORS[template.domain]

                                return (
                                  <button
                                    key={template.id}
                                    onClick={() => setSelectedTemplateId(template.id)}
                                    className={`flex items-center gap-2 w-full p-2 rounded-md transition-colors text-left ${
                                      isSelected
                                        ? 'bg-warm-200'
                                        : 'hover:bg-warm-200'
                                    }`}
                                  >
                                    <span className="text-sm flex-1 truncate">{template.name}</span>
                                    <Badge
                                      className="text-[10px] h-4 px-1.5 uppercase font-medium"
                                      style={{
                                        backgroundColor: domainStyle.bg,
                                        color: domainStyle.text,
                                        border: `1px solid ${domainStyle.border}`,
                                      }}
                                    >
                                      {template.domain}
                                    </Badge>
                                  </button>
                                )
                              })}
                            </div>
                          )}
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>
            )
          })}

          {/* Entity Pool - Only show for Doc templates */}
          {selectedTemplate && selectedTemplate.type === 'Doc' && (
            <div className="mt-4 pt-4 border-t border-border">
              <div className="px-2 mb-2">
                <h3 className="text-xs font-semibold text-text-secondary uppercase tracking-wide">
                  Entities (Drag to template →)
                </h3>
              </div>

              {/* Market Domain */}
              <div className="mb-2">
                <div className="flex items-center gap-2 px-2 py-1.5 text-xs font-medium" style={{ color: DOMAIN_COLORS.Market.text }}>
                  <Users className="w-3 h-3" />
                  <span>Market Domain</span>
                </div>
                <div className="ml-2 space-y-0.5">
                  {['Persona', 'Problem', 'CustomerPain', 'Trend'].map((entity) => (
                    <div
                      key={entity}
                      draggable
                      onDragStart={(e) => {
                        e.dataTransfer.setData('application/json', JSON.stringify({
                          type: 'entity',
                          classUri: `market:${entity}`,
                          className: entity,
                          domain: 'market',
                        }))
                      }}
                      className="flex items-center gap-2 px-2 py-1.5 text-xs cursor-grab hover:bg-warm-200 rounded transition-colors"
                    >
                      <Users className="w-3 h-3" style={{ color: DOMAIN_COLORS.Market.text }} />
                      <span>{entity}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Business Domain */}
              <div className="mb-2">
                <div className="flex items-center gap-2 px-2 py-1.5 text-xs font-medium" style={{ color: DOMAIN_COLORS.Business.text }}>
                  <Users className="w-3 h-3" />
                  <span>Business Domain</span>
                </div>
                <div className="ml-2 space-y-0.5">
                  {['Strategy', 'ValueProposition', 'Channel'].map((entity) => (
                    <div
                      key={entity}
                      draggable
                      onDragStart={(e) => {
                        e.dataTransfer.setData('application/json', JSON.stringify({
                          type: 'entity',
                          classUri: `business:${entity}`,
                          className: entity,
                          domain: 'business',
                        }))
                      }}
                      className="flex items-center gap-2 px-2 py-1.5 text-xs cursor-grab hover:bg-warm-200 rounded transition-colors"
                    >
                      <Users className="w-3 h-3" style={{ color: DOMAIN_COLORS.Business.text }} />
                      <span>{entity}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Solution Domain */}
              <div className="mb-2">
                <div className="flex items-center gap-2 px-2 py-1.5 text-xs font-medium" style={{ color: DOMAIN_COLORS.Solution.text }}>
                  <Users className="w-3 h-3" />
                  <span>Solution Domain</span>
                </div>
                <div className="ml-2 space-y-0.5">
                  {['Feature', 'Requirement', 'Epic', 'UserStory', 'AcceptanceCriteria'].map((entity) => (
                    <div
                      key={entity}
                      draggable
                      onDragStart={(e) => {
                        e.dataTransfer.setData('application/json', JSON.stringify({
                          type: 'entity',
                          classUri: `solution:${entity}`,
                          className: entity,
                          domain: 'solution',
                        }))
                      }}
                      className="flex items-center gap-2 px-2 py-1.5 text-xs cursor-grab hover:bg-warm-200 rounded transition-colors"
                    >
                      <Users className="w-3 h-3" style={{ color: DOMAIN_COLORS.Solution.text }} />
                      <span>{entity}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Right Panel - Template Editor */}
      <div className="flex-1 overflow-y-auto bg-warm-50">
        {selectedTemplate ? (
          <TemplateEditor
            template={selectedTemplate}
            onUpdate={handleTemplateUpdate}
            accountId={accountId!}
          />
        ) : (
          <div className="flex items-center justify-center h-full text-text-muted">
            <p>Select a template to edit</p>
          </div>
        )}
      </div>
    </div>
  )
}

// ============================================================================
// TEMPLATE EDITOR
// ============================================================================

interface TemplateEditorProps {
  template: ArtifactTemplate
  onUpdate: (template: ArtifactTemplate) => void
  accountId: string
}

function TemplateEditor({ template, onUpdate, accountId }: TemplateEditorProps) {
  const [activeTab, setActiveTab] = useState<EditorTab>('schema')
  const [editedTemplate, setEditedTemplate] = useState<ArtifactTemplate>(template)
  const [saving, setSaving] = useState(false)
  const [hasChanges, setHasChanges] = useState(false)

  // Reset when template changes
  useEffect(() => {
    setEditedTemplate(template)
    setHasChanges(false)
  }, [template.id])

  const handleFieldChange = (field: keyof ArtifactTemplate, value: unknown) => {
    setEditedTemplate((prev) => ({ ...prev, [field]: value }))
    setHasChanges(true)
  }

  const handleStructureChange = (structure: ArtifactTemplate['structure']) => {
    setEditedTemplate((prev) => ({ ...prev, structure }))
    setHasChanges(true)
  }

  const handleSave = async () => {
    try {
      setSaving(true)
      const result = await artifactTemplatesApi.update(accountId, editedTemplate.id, {
        name: editedTemplate.name,
        description: editedTemplate.description || undefined,
        structure: editedTemplate.structure as Record<string, unknown>,
        isActive: editedTemplate.isActive,
      })
      onUpdate(result.template)
      setHasChanges(false)
    } catch {
      // Error handled silently - save button stays enabled for retry
    } finally {
      setSaving(false)
    }
  }

  const handleReset = () => {
    setEditedTemplate(template)
    setHasChanges(false)
  }

  const domainStyle = DOMAIN_COLORS[template.domain]
  const schemaTabLabel = template.type === 'Canvas' ? 'Schema' : 'Sections'

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="p-6 border-b border-border bg-white">
        <div className="flex items-start gap-4">
          <div
            className="w-11 h-11 rounded-lg flex items-center justify-center"
            style={{ backgroundColor: domainStyle.bg }}
          >
            <span style={{ color: domainStyle.text }}>{TYPE_ICONS[template.type]}</span>
          </div>
          <div className="flex-1">
            <h2 className="font-display font-semibold text-lg">{template.name}</h2>
            <p className="text-sm text-text-muted mt-0.5">{template.description}</p>
          </div>
          <Badge
            className="text-xs uppercase font-medium"
            style={{
              backgroundColor: domainStyle.bg,
              color: domainStyle.text,
              border: `1px solid ${domainStyle.border}`,
            }}
          >
            {template.domain}
          </Badge>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mt-6 border-b border-border -mb-[1px]">
          {(['details', 'schema', 'defaults'] as EditorTab[]).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab
                  ? 'border-business text-business'
                  : 'border-transparent text-text-muted hover:text-text-secondary'
              }`}
            >
              {tab === 'schema' ? schemaTabLabel : tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      <div className="flex-1 overflow-y-auto p-6">
        {activeTab === 'details' && (
          <DetailsTab template={editedTemplate} onChange={handleFieldChange} />
        )}
        {activeTab === 'schema' && (
          <SchemaTab template={editedTemplate} onStructureChange={handleStructureChange} />
        )}
        {activeTab === 'defaults' && (
          <DefaultsTab template={editedTemplate} onChange={handleFieldChange} />
        )}
      </div>

      {/* Footer */}
      <div className="p-4 border-t border-border bg-warm-50 flex items-center justify-between">
        <Button variant="ghost" onClick={handleReset} disabled={!hasChanges}>
          <RotateCcw className="w-4 h-4" />
          Reset to Default
        </Button>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleReset} disabled={!hasChanges}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={!hasChanges || saving}>
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            Save Changes
          </Button>
        </div>
      </div>
    </div>
  )
}

// ============================================================================
// DETAILS TAB
// ============================================================================

interface DetailsTabProps {
  template: ArtifactTemplate
  onChange: (field: keyof ArtifactTemplate, value: unknown) => void
}

function DetailsTab({ template, onChange }: DetailsTabProps) {
  return (
    <div className="space-y-6 max-w-xl">
      <div className="space-y-2">
        <Label htmlFor="name">Template Name</Label>
        <Input
          id="name"
          value={template.name}
          onChange={(e) => onChange('name', e.target.value)}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="description">Description</Label>
        <Textarea
          id="description"
          value={template.description || ''}
          onChange={(e) => onChange('description', e.target.value)}
          rows={3}
        />
      </div>

      <div className="space-y-2">
        <Label>Domain</Label>
        <div className="flex gap-2">
          {(['Market', 'Business', 'Solution'] as ArtifactDomain[]).map((domain) => {
            const style = DOMAIN_COLORS[domain]
            const isSelected = template.domain === domain
            return (
              <button
                key={domain}
                onClick={() => onChange('domain', domain)}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  isSelected ? 'ring-2 ring-offset-2' : ''
                }`}
                style={{
                  backgroundColor: style.bg,
                  color: style.text,
                  borderColor: style.border,
                }}
              >
                {domain}
              </button>
            )
          })}
        </div>
      </div>

      {template.isSystem && (
        <div className="p-3 bg-warm-100 rounded-lg border border-border-subtle">
          <p className="text-sm text-text-muted">
            This is a system template. Some fields may be restricted.
          </p>
        </div>
      )}
    </div>
  )
}

// ============================================================================
// SCHEMA TAB - Routes to Canvas or Doc editor
// ============================================================================

interface SchemaTabProps {
  template: ArtifactTemplate
  onStructureChange: (structure: ArtifactTemplate['structure']) => void
}

function SchemaTab({ template, onStructureChange }: SchemaTabProps) {
  if (template.type === 'Canvas') {
    return (
      <CanvasSchemaEditor
        structure={template.structure as CanvasTemplateStructure}
        onChange={onStructureChange}
      />
    )
  }

  if (template.type === 'Doc') {
    return (
      <DocSectionsEditor
        structure={template.structure as DocTemplateStructure}
        onChange={onStructureChange}
      />
    )
  }

  // Plan and Evidence - show raw JSON for now
  return (
    <div className="space-y-4">
      <p className="text-sm text-text-muted">
        Structure configuration for {template.type} templates.
      </p>
      <pre className="p-4 bg-warm-100 rounded-lg text-xs overflow-auto max-h-96">
        {JSON.stringify(template.structure, null, 2)}
      </pre>
    </div>
  )
}

// ============================================================================
// CANVAS SCHEMA EDITOR
// ============================================================================

interface CanvasSchemaEditorProps {
  structure: CanvasTemplateStructure
  onChange: (structure: CanvasTemplateStructure) => void
}

function CanvasSchemaEditor({ structure, onChange }: CanvasSchemaEditorProps) {
  const [selectedSectionId, setSelectedSectionId] = useState<string | null>(
    structure.sections[0]?.id || null
  )

  const selectedSection = structure.sections.find((s) => s.id === selectedSectionId)

  const handleSectionChange = (sectionId: string, field: keyof CanvasSection, value: unknown) => {
    const updatedSections = structure.sections.map((s) =>
      s.id === sectionId ? { ...s, [field]: value } : s
    )
    onChange({ ...structure, sections: updatedSections })
  }

  // Parse grid areas into a visual representation
  const gridStyle = {
    display: 'grid',
    gridTemplateColumns: `repeat(${structure.gridColumns}, 1fr)`,
    gridTemplateRows: `repeat(${structure.gridRows}, 60px)`,
    gridTemplateAreas: structure.gridAreas,
    gap: '8px',
  }

  return (
    <div className="space-y-6">
      <p className="text-sm text-text-secondary">
        Define the canvas sections and their grid layout. Click a section to edit.
      </p>

      {/* Visual Grid */}
      <div className="p-4 bg-warm-100 rounded-lg border border-border">
        <div style={gridStyle}>
          {structure.sections.map((section) => {
            const isSelected = section.id === selectedSectionId
            return (
              <button
                key={section.id}
                onClick={() => setSelectedSectionId(section.id)}
                className={`p-3 bg-white rounded-md border text-left transition-all ${
                  isSelected
                    ? 'border-business ring-2 ring-business/20'
                    : 'border-border hover:border-business/50'
                }`}
                style={{ gridArea: section.gridArea }}
              >
                <div
                  className="text-xs font-medium flex items-center gap-1.5"
                  style={{ color: section.color }}
                >
                  {section.title}
                </div>
                <div className="text-[10px] text-text-muted mt-1 font-mono">
                  {section.classUris.join(', ') || 'No entity mapping'}
                </div>
              </button>
            )
          })}
        </div>
      </div>

      {/* Section Editor */}
      {selectedSection && (
        <Card className="border-border">
          <CardContent className="p-4 space-y-4">
            <h4 className="font-medium text-sm">Section: {selectedSection.title}</h4>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="section-id">Section ID</Label>
                <Input
                  id="section-id"
                  value={selectedSection.id}
                  className="font-mono text-sm"
                  readOnly
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="section-gridArea">Grid Area</Label>
                <Input
                  id="section-gridArea"
                  value={selectedSection.gridArea}
                  onChange={(e) =>
                    handleSectionChange(selectedSection.id, 'gridArea', e.target.value)
                  }
                  className="font-mono text-sm"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="section-title">Title</Label>
              <Input
                id="section-title"
                value={selectedSection.title}
                onChange={(e) => handleSectionChange(selectedSection.id, 'title', e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="section-classUris">Entity Class URIs (comma-separated)</Label>
              <Input
                id="section-classUris"
                value={selectedSection.classUris.join(', ')}
                onChange={(e) =>
                  handleSectionChange(
                    selectedSection.id,
                    'classUris',
                    e.target.value.split(',').map((s) => s.trim()).filter(Boolean)
                  )
                }
                className="font-mono text-sm"
                placeholder="e.g., business:KeyPartnership, market:Segment"
              />
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}

// ============================================================================
// DOCUMENT SECTIONS EDITOR
// ============================================================================

interface DocSectionsEditorProps {
  structure: DocTemplateStructure
  onChange: (structure: DocTemplateStructure) => void
}

function DocSectionsEditor({ structure, onChange }: DocSectionsEditorProps) {
  // Ensure entityShortcuts is initialized
  const entityShortcuts = structure.entityShortcuts || []

  const handleSectionChange = (index: number, field: keyof DocSection, value: unknown) => {
    const updatedSections = [...structure.sections]
    updatedSections[index] = { ...updatedSections[index], [field]: value }
    onChange({ ...structure, sections: updatedSections })
  }

  const handleAddSection = () => {
    const newSection: DocSection = {
      id: `section-${Date.now()}`,
      heading: 'New Section',
      description: 'Section description',
      required: false,
    }
    onChange({ ...structure, sections: [...structure.sections, newSection] })
  }

  const handleMoveSection = (index: number, direction: 'up' | 'down') => {
    const newIndex = direction === 'up' ? index - 1 : index + 1
    if (newIndex < 0 || newIndex >= structure.sections.length) return

    const updatedSections = [...structure.sections]
    const [removed] = updatedSections.splice(index, 1)
    updatedSections.splice(newIndex, 0, removed)
    onChange({ ...structure, sections: updatedSections })
  }

  // Entity shortcuts handlers
  const handleRemoveEntityShortcut = (index: number) => {
    const updated = [...entityShortcuts]
    updated.splice(index, 1)
    onChange({ ...structure, entityShortcuts: updated })
  }

  const handleDropEntity = (e: React.DragEvent) => {
    e.preventDefault()
    try {
      const data = JSON.parse(e.dataTransfer.getData('application/json'))
      if (data.type === 'entity') {
        // Check if already exists
        const exists = entityShortcuts.some((s) => s.classUri === data.classUri)
        if (exists) return

        const newShortcut: EntityShortcut = {
          classUri: data.classUri,
          className: data.className,
          domain: data.domain,
          isQuickAdd: true,
          sortOrder: entityShortcuts.length,
        }

        onChange({ ...structure, entityShortcuts: [...entityShortcuts, newShortcut] })
      }
    } catch (err) {
      // Invalid drag data
      console.error('Invalid drag data:', err)
    }
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'copy'
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-text-secondary">
        Document sections define the structure template. Entity shortcuts enable quick-add functionality in the editor sidebar.
      </p>

      {/* Two-Column Grid */}
      <div className="grid grid-cols-2 gap-4">
        {/* Left Column: Sections List */}
        <div className="space-y-3">
          <h3 className="text-sm font-semibold uppercase tracking-wide text-text-secondary flex items-center gap-2">
            <FileText className="w-4 h-4" />
            Document Sections
            <span className="text-xs font-normal text-text-muted ml-auto">{structure.sections.length} sections</span>
          </h3>
          <div className="border border-border rounded-lg overflow-hidden">
        {structure.sections.map((section, index) => (
          <div
            key={section.id}
            className="flex items-center gap-3 p-3 bg-white border-b border-border-subtle last:border-b-0 hover:bg-warm-50 transition-colors"
          >
            {/* Drag Handle */}
            <div className="flex flex-col gap-0.5">
              <button
                onClick={() => handleMoveSection(index, 'up')}
                disabled={index === 0}
                className="p-0.5 text-text-muted hover:text-text-secondary disabled:opacity-30"
              >
                <ChevronDown className="w-3 h-3 rotate-180" />
              </button>
              <button
                onClick={() => handleMoveSection(index, 'down')}
                disabled={index === structure.sections.length - 1}
                className="p-0.5 text-text-muted hover:text-text-secondary disabled:opacity-30"
              >
                <ChevronDown className="w-3 h-3" />
              </button>
            </div>
            <GripVertical className="w-4 h-4 text-text-muted cursor-grab" />

            {/* Section Info */}
            <div className="flex-1 min-w-0">
              <div className="font-medium text-sm">{section.heading}</div>
              <div className="text-xs text-text-muted truncate">{section.description}</div>
            </div>

            {/* Required Badge */}
            {section.required && (
              <Badge
                className="text-xs"
                style={{
                  backgroundColor: DOMAIN_COLORS.Solution.bg,
                  color: DOMAIN_COLORS.Solution.text,
                  border: `1px solid ${DOMAIN_COLORS.Solution.border}`,
                }}
              >
                Required
              </Badge>
            )}

            {/* Edit Button */}
            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0"
              onClick={() => {
                // Could open a modal for editing
                const newHeading = prompt('Section heading:', section.heading)
                if (newHeading) handleSectionChange(index, 'heading', newHeading)
              }}
            >
              <Pencil className="w-3.5 h-3.5" />
            </Button>

            {/* Toggle Required */}
            <button
              onClick={() => handleSectionChange(index, 'required', !section.required)}
              className={`w-5 h-5 rounded border flex items-center justify-center transition-colors ${
                section.required
                  ? 'bg-business border-business text-white'
                  : 'border-border-subtle hover:border-business'
              }`}
            >
              {section.required && (
                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                </svg>
              )}
            </button>
          </div>
        ))}
          </div>

          {/* Add Section Button */}
          <Button variant="outline" onClick={handleAddSection} className="w-full">
            <Plus className="w-4 h-4" />
            Add Section
          </Button>
        </div>

        {/* Right Column: Entity Shortcuts */}
        <div className="space-y-3">
          <h3 className="text-sm font-semibold uppercase tracking-wide text-text-secondary flex items-center gap-2">
            <Users className="w-4 h-4" />
            Entity Shortcuts
            <span className="text-xs font-normal text-text-muted ml-auto">{entityShortcuts.length} shortcuts</span>
          </h3>

          {entityShortcuts.length === 0 ? (
            <div
              onDrop={handleDropEntity}
              onDragOver={handleDragOver}
              className="border-2 border-dashed border-border rounded-lg p-8 text-center bg-warm-50 transition-colors hover:border-business hover:bg-business/5"
            >
              <Users className="w-8 h-8 text-text-muted mx-auto mb-2 opacity-50" />
              <p className="text-sm text-text-muted">No entity shortcuts configured</p>
              <p className="text-xs text-text-muted mt-1">Entity shortcuts will enable quick-add functionality in Smart Docs editor</p>
            </div>
          ) : (
            <div
              onDrop={handleDropEntity}
              onDragOver={handleDragOver}
              className="border border-border rounded-lg overflow-hidden"
            >
              {entityShortcuts.map((shortcut, index) => {
                const domainColor = shortcut.domain === 'market'
                  ? DOMAIN_COLORS.Market
                  : shortcut.domain === 'business'
                  ? DOMAIN_COLORS.Business
                  : DOMAIN_COLORS.Solution;

                return (
                  <div
                    key={shortcut.classUri}
                    className="flex items-center gap-3 p-3 bg-white border-b border-border-subtle last:border-b-0 hover:bg-warm-50 transition-colors"
                  >
                    <GripVertical className="w-4 h-4 text-text-muted cursor-grab" />
                    <Users className="w-4 h-4" style={{ color: domainColor.text }} />

                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-sm">{shortcut.className}</div>
                      <div className="text-xs capitalize" style={{ color: domainColor.text }}>
                        {shortcut.domain}
                      </div>
                    </div>

                    {/* Remove Button */}
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0 hover:bg-red-50"
                      onClick={() => handleRemoveEntityShortcut(index)}
                    >
                      <X className="w-3.5 h-3.5 text-text-muted hover:text-red-600" />
                    </Button>
                  </div>
                );
              })}
            </div>
          )}

          <p className="text-xs text-text-muted">
            Drag entities from the sidebar to add quick-add shortcuts
          </p>
        </div>
      </div>
    </div>
  )
}

// ============================================================================
// DEFAULTS TAB
// ============================================================================

interface DefaultsTabProps {
  template: ArtifactTemplate
  onChange: (field: keyof ArtifactTemplate, value: unknown) => void
}

function DefaultsTab({ template, onChange }: DefaultsTabProps) {
  return (
    <div className="space-y-4">
      <p className="text-sm text-text-muted">
        Configure default content that will be pre-populated when creating new artifacts from this template.
      </p>

      <div className="p-4 bg-warm-100 rounded-lg border border-border-subtle">
        <pre className="text-xs overflow-auto max-h-96">
          {template.defaultContent
            ? JSON.stringify(template.defaultContent, null, 2)
            : 'No default content configured'}
        </pre>
      </div>

      <p className="text-xs text-text-muted">
        Default content editing will be available in a future update.
      </p>
    </div>
  )
}
